import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote, Loader2, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { TESTIMONIALS } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", location: "", rating: 0, text: "" };

export const Testimonials = () => {
  const [reviews, setReviews] = useState(TESTIMONIALS);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/reviews`)
      .then(({ data }) => {
        if (active && Array.isArray(data) && data.length) {
          setReviews([...data, ...TESTIMONIALS]);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || form.rating < 1 || form.text.trim().length < 3) {
      toast.error("Please add your name, a star rating and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, {
        name: form.name.trim(),
        location: form.location.trim(),
        rating: form.rating,
        text: form.text.trim(),
      });
      toast.success(data?.message || "Thanks for your review!");
      setForm(initialForm);
      setHover(0);
      setOpen(false);
    } catch (err) {
      toast.error("Sorry, something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="reviews"
      data-testid="testimonials-section"
      className="py-20 sm:py-28 lg:py-32 bg-green-50/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-3xl">
            <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest">
              What customers say
            </span>
            <h2
              data-testid="testimonials-headline"
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-green-950 tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Trusted by local homeowners.
            </h2>
            <p className="mt-4 text-slate-600 text-base lg:text-lg">
              Reliable, friendly and professional pond and garden care — here's what people say.
            </p>
          </div>
          <Button
            data-testid="leave-review-button"
            onClick={() => setOpen(true)}
            className="bg-green-700 hover:bg-green-800 text-white rounded-full px-7 py-6 text-base font-semibold shrink-0"
          >
            <PenLine className="w-4 h-4 mr-2" />
            Leave a Review
          </Button>
        </div>

        <Carousel
          opts={{ align: "start", loop: true }}
          className="w-full"
          data-testid="reviews-carousel"
        >
          <CarouselContent className="-ml-4">
            {reviews.map((t, i) => (
              <CarouselItem
                key={i}
                className="pl-4 md:basis-1/2 lg:basis-1/3"
              >
                <article
                  data-testid={`testimonial-card-${i}`}
                  className="relative h-full bg-white rounded-2xl p-7 border border-green-100 hover:border-green-300 hover:shadow-lg hover:shadow-green-900/10 transition-all flex flex-col"
                >
                  <Quote className="absolute top-5 right-5 w-8 h-8 text-green-100" />
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, k) => (
                      <Star key={k} className="w-5 h-5 text-amber-400" fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-green-950 leading-relaxed text-base flex-1 font-medium">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="mt-6 pt-4 border-t border-green-100">
                    <div
                      className="font-semibold text-green-950"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {t.name}
                    </div>
                    {t.location ? (
                      <div className="text-xs text-slate-500">{t.location}</div>
                    ) : null}
                  </div>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-end gap-3 mt-8">
            <CarouselPrevious
              data-testid="reviews-prev"
              className="static translate-y-0 h-11 w-11 border-green-200 text-green-700 hover:bg-green-700 hover:text-white"
            />
            <CarouselNext
              data-testid="reviews-next"
              className="static translate-y-0 h-11 w-11 border-green-200 text-green-700 hover:bg-green-700 hover:text-white"
            />
          </div>
        </Carousel>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              Leave a review
            </DialogTitle>
            <DialogDescription>
              We'd love to hear how we did. Your review is sent to Nathan for a quick check before it appears.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
            <div>
              <Label htmlFor="review-name">Your name</Label>
              <Input
                id="review-name"
                data-testid="review-name-input"
                value={form.name}
                onChange={handleChange("name")}
                placeholder="e.g. Sarah W."
                className="mt-1.5"
                maxLength={80}
              />
            </div>

            <div>
              <Label htmlFor="review-location">Location (optional)</Label>
              <Input
                id="review-location"
                data-testid="review-location-input"
                value={form.location}
                onChange={handleChange("location")}
                placeholder="e.g. East Grinstead"
                className="mt-1.5"
                maxLength={80}
              />
            </div>

            <div>
              <Label>Your rating</Label>
              <div className="mt-1.5 flex gap-1" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`review-star-${n}`}
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    className="p-1"
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        (hover || form.rating) >= n
                          ? "text-amber-400"
                          : "text-slate-300"
                      }`}
                      fill={(hover || form.rating) >= n ? "currentColor" : "none"}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="review-text">Your review</Label>
              <Textarea
                id="review-text"
                data-testid="review-text-input"
                value={form.text}
                onChange={handleChange("text")}
                placeholder="Tell us about the work Nathan did…"
                className="mt-1.5 min-h-[110px]"
                maxLength={1000}
              />
            </div>

            <Button
              type="submit"
              data-testid="review-submit-button"
              disabled={submitting}
              className="w-full bg-green-700 hover:bg-green-800 rounded-full py-6 text-base font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                </>
              ) : (
                "Submit review"
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Testimonials;
